import { ParsedUrlQuery } from "querystring"

import {
  Cell as CellPB,
  Cell_Type as CellTypePB,
  Cell_Width as CellWidthPB,
  Chip_Type as ChipType,
  Report,
} from "gen/analytics/models_pb"
import {
  Filter,
  Filter_Type as FilterType,
  Filters,
  Filter_RangeType as RangeType,
} from "gen/analyticsv2/models_pb"
import { Timestamp as Timestamppb } from "gen/google/protobuf/timestamp_pb"
import { Decimal as PBDecimal } from "gen/proto/decimal/models_pb"
import { Money as MoneyPB } from "gen/proto/money/models_pb"
import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { Money } from "shared-ts/classes/money"
import { Format, Timestamp } from "shared-ts/classes/timestamp"
import { UUID } from "shared-ts/classes/uuid"
import { TimestampRange } from "shared-ts/libs/dates/timestampRange"

import { queryParamsToDate } from "lib/params"
import { urls } from "urls"

import {
  ACCOUNT_PARAM,
  CLASS_PARAM,
  CLERK_PARAM,
  CUSTOMER_PARAM,
  CUSTOMER_TAG_PARAM,
  DATE_RANGE_PARAM,
  DEPARTMENT_PARAM,
  END_DATE_PARAM,
  ENTITY_PARAM,
  FEE_PARAM,
  FINELINE_PARAM,
  JOB_PARAM,
  LOCATION_PARAM,
  METHOD_PARAM,
  PARAM_DELIMITER,
  PRODUCT_PARAM,
  PRODUCT_TAG_PARAM,
  REP_PARAM,
  SECONDARY_LOCATION_PARAM,
  START_DATE_PARAM,
  TAX_RATE_PARAM,
  TERMS_PARAM,
  VENDOR_PARAM,
} from "../params"
import {
  Cell,
  ChipCell,
  DateRange,
  FrontendReportConfig,
  ReportConfig,
  ReportData,
  Row,
  Sort,
  TextCell,
} from "../types"

export const TOTAL_KEY = "total"

export const clientTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone

export type queryParamValue = string | string[] | undefined
export function isString(value: queryParamValue): value is string {
  return typeof value === "string"
}

export function extractParams(paramsString: queryParamValue): string[] {
  if (!isString(paramsString)) {
    return []
  }

  return paramsString.split(PARAM_DELIMITER)
}

function getUUIDs(idsString: queryParamValue): PBUUID[] {
  return extractParams(idsString).map((idString) => UUID.fromString(idString))
}

function getDate(dateString: queryParamValue): Date | undefined {
  if (!isString(dateString)) {
    return undefined
  }

  return queryParamsToDate(dateString) ?? undefined
}

export function getDateRangeFilter(
  c: ReportConfig,
  startDateString: queryParamValue,
  endDateString: queryParamValue,
  rangeDateString: queryParamValue
): SerializableFilter | undefined {
  const start = getDate(startDateString)
  const end = getDate(endDateString)
  const range = getDateRange(rangeDateString)

  switch (c.dateFilter.type) {
    case "date":
      if (c.dateFilter.side === "start") {
        return {
          type: FilterType.TIMESTAMP_RANGE,
          rangeType: RangeType.START_ONLY,
          clientTimezone: clientTimeZone(),
          clientDateRange: {
            start: start,
            end: undefined,
          },
          ids: [],
          stringIds: [],
        }
      } else {
        return {
          type: FilterType.TIMESTAMP_RANGE,
          rangeType: RangeType.END_ONLY,
          clientTimezone: clientTimeZone(),
          clientDateRange: {
            start: undefined,
            end: end,
          },
          ids: [],
          stringIds: [],
        }
      }
    case "daterange":
      return {
        type: FilterType.TIMESTAMP_RANGE,
        rangeType: RangeType.FULL,
        clientTimezone: clientTimeZone(),
        clientDateRange: range,
        ids: [],
        stringIds: [],
      }
  }
}

function getDateRange(
  dateRangeString: string | string[] | undefined
): DateRange | undefined {
  if (typeof dateRangeString !== "string") {
    return undefined
  }

  const tr = TimestampRange.fromQueryParam(dateRangeString)

  return tr?.start && tr.end
    ? {
        start: Timestamppb.toDate(tr.start),
        end: Timestamppb.toDate(tr.end),
      }
    : undefined
}

export function extractBytes({ bytes }: { bytes: Uint8Array }): Uint8Array {
  return bytes
}

export function timeSort(label: string, value: number): Sort {
  return {
    label,
    value,
    ascSortType: "Oldest",
    descSortType: "Newest",
  }
}

export function numericalSort(label: string, value: number): Sort {
  return {
    label,
    value,
    ascSortType: "Low to High",
    descSortType: "High to Low",
  }
}

export function alphabeticSort(label: string, value: number): Sort {
  return {
    label,
    value,
    ascSortType: "A to Z",
    descSortType: "Z to A",
  }
}

function textCell(cell: Partial<TextCell>): TextCell {
  return {
    type: "text",
    width: "base",
    font: "base",
    value: "",
    ...cell,
  }
}
export function baseCell(value?: string): Cell {
  return textCell({ value })
}

export function baseDecimalCell(value?: PBDecimal): Cell {
  return baseCell(
    (value ? Decimal.fromPB(value) : Decimal.zero()).toPercentString(1)
  )
}

export function baseNumberCell(value: number): Cell {
  return baseCell(value.toString())
}

export function baseMoneyCell(m?: MoneyPB, hideZero?: boolean): Cell {
  const money = Money.fromPB(m)
  return baseCell(money.isZero() && hideZero ? "" : money.format())
}

export function boldCell(value?: string): Cell {
  return textCell({ value, font: "bold" })
}

export function dateCell(value?: Timestamppb): Cell {
  const val = Timestamp.format(Format.DATE_TIME, value)
  return textCell({ value: val, width: "medium" })
}

export function longCell(value?: string): Cell {
  return textCell({ value, width: "medium", font: "bold" })
}

export function parseReport(report?: Report): ReportData {
  const summaryLabels: string[] = []
  const summaryValues: { [summaryLabel: string]: string } = {}
  for (const { label, value } of report?.summaries ?? []) {
    summaryLabels.push(label)
    summaryValues[label] = value
  }

  const rows: Row[] = []
  for (const { key, cellsByHeader } of report?.rows ?? []) {
    const cells: {
      [headerKey: string]: Cell
    } = {}
    for (const header in cellsByHeader) {
      cells[header] = parseCell(cellsByHeader[header])
    }
    rows.push({
      key: key,
      cells: cells,
    })
  }

  return {
    headersLabels: report?.headers,
    summaryLabels: summaryLabels,
    summaryValues: summaryValues,
    rows: rows,
  }
}

function parseCell(cell?: CellPB): Cell {
  if (cell?.type == CellTypePB.CHIP) {
    return parseChipCell(cell)
  }

  let width: "base" | "medium" | "long" = "base"
  switch (cell?.width) {
    case CellWidthPB.LONG:
      width = "long"
      break
    case CellWidthPB.MEDIUM:
      width = "medium"
      break
  }
  return {
    value: cell?.value ?? "",
    type: "text",
    font: cell?.type == CellTypePB.BOLD ? "bold" : "base",
    width,
  }
}

// eslint-disable-next-line complexity
function parseChipCell(cell: CellPB): Cell {
  if (cell.value == "") {
    return baseCell("")
  }
  const chipCell: Cell = {
    type: "chip",
    url: "",
    color: "gray",
    value: cell.value,
  }
  const chipSettings: Record<ChipType, Partial<ChipCell>> = {
    [ChipType.RETURN]: {
      color: "yellow",
      url: urls.sale(cell.chip?.return?.saleId),
    },
    [ChipType.SALE]: {
      color: "green",
      url: urls.sale(cell.chip?.sale?.saleId),
    },
    [ChipType.PRODUCT]: {
      url: urls.product(cell.chip?.product?.productId),
      color: "blue",
    },
    [ChipType.CUSTOMER]: {
      url: urls.customer(cell.chip?.customer?.customerId),
      color: "sky",
    },
    [ChipType.LOCATION]: {
      color: "gray",
    },
    [ChipType.ACCOUNT]: {
      color: "gray",
    },
    [ChipType.BILL_PAYMENT]: {
      color: "yellow",
      url: urls.bill(cell.chip?.billPayment?.billPaymentId, false),
    },
    [ChipType.BILL_REFUND]: {
      color: "red",
      url: urls.bill(cell.chip?.billRefund?.billPaymentId, true),
    },
    [ChipType.ORDER_SHIPMENT]: {
      color: "gray",
      url: urls.orderShipment(cell.chip?.orderShipment?.orderShipmentId),
    },
    [ChipType.TRANSFER]: {
      color: "gray",
      url: urls.transfer(cell.chip?.transfer?.transferId),
    },
    [ChipType.VENDOR]: {
      color: "gray",
      url: urls.vendor(cell.chip?.vendor?.vendorId),
    },
    [ChipType.COUNT]: {
      color: "gray",
      url: urls.inventoryCount(cell.chip?.count?.countId),
    },
    [ChipType.UNDEFINED]: {},
    [ChipType.CLOSE_OUT]: {},
    [ChipType.PETTY_CASH]: {},
    [ChipType.ORDER]: {
      color: "gray",
      url: urls.order(cell.chip?.order?.orderId),
    },
    [ChipType.FINANCE_CHARGE]: {
      color: "green",
      url: urls.financeCharge(cell.chip?.financeCharge?.financeChargeId),
    },
    [ChipType.JOB]: {
      url: urls.job(cell.chip?.job?.jobId),
      color: "sky",
    },
    [ChipType.ENTITY]: {},
    [ChipType.EXTERNAL_TRANSACTION]: {
      color: "gray",
      url: urls.adminFinancesExternalTransaction(
        UUID.fromPBOrUndefined(
          cell.chip?.externalTransaction?.externalTransactionId
        )
      ),
    },
    [ChipType.INTERNAL_TRANSACTION]: {
      color: "gray",
      url: urls.adminFinancesInternalTransaction(cell.value),
    },
    [ChipType.MANUAL_JOURNAL_ENTRY]: {
      color: "gray",
    },
  }
  return { ...chipCell, ...chipSettings[cell.chip?.type ?? ChipType.UNDEFINED] }
}

export type SerializableReq<T> = Omit<T, "filters"> & {
  safeFilters: SerializableFilters
}

type ReqWithFilters<T> = Omit<SerializableReq<T>, "safeFilters"> & {
  filters: Filters
}

type SerializableFilters = {
  locationFilter?: SerializableFilter
  secondaryLocationFilter?: SerializableFilter
  productFilter?: SerializableFilter
  productTagFilter?: SerializableFilter
  customerFilter?: SerializableFilter
  jobFilter?: SerializableFilter
  customerTagFilter?: SerializableFilter
  vendorFilter?: SerializableFilter
  departmentFilter?: SerializableFilter
  classFilter?: SerializableFilter
  finelineFilter?: SerializableFilter
  clerkFilter?: SerializableFilter
  repFilter?: SerializableFilter
  dateRangeFilter?: SerializableFilter
  accountFilter?: SerializableFilter
  termsFilter?: SerializableFilter
  taxRateFilter?: SerializableFilter
  entityFilter?: SerializableFilter
  methodFilter?: SerializableFilter
  feeFilter?: SerializableFilter
}

export function restoreSerializableReq<T>(
  req: SerializableReq<T>
): ReqWithFilters<T> {
  return {
    ...req,
    filters: {
      timestampRangeFilter: restoreSerializableFilter(
        req.safeFilters.dateRangeFilter
      ),
      staffFilter: restoreSerializableFilter(req.safeFilters.clerkFilter),
      locationFilter: restoreSerializableFilter(req.safeFilters.locationFilter),
      secondaryLocationFilter: restoreSerializableFilter(
        req.safeFilters.secondaryLocationFilter
      ),
      productFilter: restoreSerializableFilter(req.safeFilters.productFilter),
      productTagFilter: restoreSerializableFilter(
        req.safeFilters.productTagFilter
      ),
      customerFilter: restoreSerializableFilter(req.safeFilters.customerFilter),
      jobFilter: restoreSerializableFilter(req.safeFilters.jobFilter),
      customerTagFilter: restoreSerializableFilter(
        req.safeFilters.customerTagFilter
      ),
      vendorFilter: restoreSerializableFilter(req.safeFilters.vendorFilter),
      departmentFilter: restoreSerializableFilter(
        req.safeFilters.departmentFilter
      ),
      classFilter: restoreSerializableFilter(req.safeFilters.classFilter),
      finelineFilter: restoreSerializableFilter(req.safeFilters.finelineFilter),
      repFilter: restoreSerializableFilter(req.safeFilters.repFilter),
      clerkFilter: restoreSerializableFilter(req.safeFilters.clerkFilter),
      accountFilter: restoreSerializableFilter(req.safeFilters.accountFilter),
      termsFilter: restoreSerializableFilter(req.safeFilters.termsFilter),
      taxRateFilter: restoreSerializableFilter(req.safeFilters.taxRateFilter),
      entityFilter: restoreSerializableFilter(req.safeFilters.entityFilter),
      methodFilter: restoreSerializableFilter(req.safeFilters.methodFilter),
      feeFilter: restoreSerializableFilter(req.safeFilters.feeFilter),
    },
  }
}

export type SerializableFilter = Omit<Filter, "clientTimestampRange"> & {
  clientDateRange?: DateRange
}

export function parseSerializableFilters(
  p: ParsedUrlQuery,
  c: ReportConfig
): SerializableFilters {
  return {
    locationFilter: idFilter(p[LOCATION_PARAM]),
    secondaryLocationFilter: idFilter(p[SECONDARY_LOCATION_PARAM]),
    productFilter: idFilter(p[PRODUCT_PARAM]),
    productTagFilter: idFilter(p[PRODUCT_TAG_PARAM]),
    customerFilter: idFilter(p[CUSTOMER_PARAM]),
    jobFilter: idFilter(p[JOB_PARAM]),
    customerTagFilter: idFilter(p[CUSTOMER_TAG_PARAM]),
    vendorFilter: idFilter(p[VENDOR_PARAM]),
    departmentFilter: idFilter(p[DEPARTMENT_PARAM]),
    classFilter: idFilter(p[CLASS_PARAM]),
    finelineFilter: idFilter(p[FINELINE_PARAM]),
    clerkFilter: idFilter(p[CLERK_PARAM]),
    repFilter: idFilter(p[REP_PARAM]),
    accountFilter: idFilter(p[ACCOUNT_PARAM]),
    termsFilter: idFilter(p[TERMS_PARAM]),
    dateRangeFilter: getDateRangeFilter(
      c,
      p[START_DATE_PARAM],
      p[END_DATE_PARAM],
      p[DATE_RANGE_PARAM]
    ),
    taxRateFilter: idFilter(p[TAX_RATE_PARAM]),
    entityFilter: idFilter(p[ENTITY_PARAM]),
    methodFilter: idFilter(p[METHOD_PARAM]),
    feeFilter: stringIdFilter(p[FEE_PARAM]),
  }
}

export function idFilter(idsString: queryParamValue): SerializableFilter {
  const ids = getUUIDs(idsString)
  return Filter.create({
    type: FilterType.ID,
    ids: ids,
  })
}

export function stringIdFilter(idsString: queryParamValue): SerializableFilter {
  return Filter.create({
    type: FilterType.ID_STRING,
    stringIds: extractParams(idsString).filter(Boolean),
  })
}

function restoreSerializableFilter(
  filter: SerializableFilter | undefined
): Filter | undefined {
  const start = filter?.clientDateRange?.start
  const end = filter?.clientDateRange?.end
  return Filter.create({
    type: filter?.type,
    rangeType: filter?.rangeType,
    clientTimezone: filter?.clientTimezone,
    ids: filter?.ids,
    stringIds: filter?.stringIds,
    clientTimestampRange: filter?.clientDateRange
      ? {
          start: start ? Timestamppb.fromDate(start) : undefined,
          end: end ? Timestamppb.fromDate(end) : undefined,
        }
      : undefined,
  })
}

function isFrontendReportConfig(c: ReportConfig): c is FrontendReportConfig {
  return !c.useReportLabels
}

export function getTableHeaders(
  c: ReportConfig,
  reportHeaders?: string[]
): string[] {
  if (isFrontendReportConfig(c)) {
    return c.tableHeaders ?? []
  }
  return reportHeaders ?? []
}

export function getSummaryLabels(
  c: ReportConfig,
  reportSummaryLabels?: string[]
): string[] {
  if (isFrontendReportConfig(c)) {
    return c.summaryLabels
  }
  return reportSummaryLabels ?? []
}
